import { Button } from "@/components/ui/button";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Menu } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { SignInButton, SignUpButton } from "@clerk/react";
import { BrandWordmark } from "./BrandWordmark";
import { OptimizedImage } from "@/components/ui/optimized-image";

export function AppNavbar({ transparent = false }: { transparent?: boolean }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (!transparent) return;
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [transparent]);

  const isScrolled = transparent && scrolled;

  return (
    <nav aria-label="Main navigation" className={`sticky top-0 z-[100] border-b transition-[background-color,border-color,backdrop-filter] duration-300 ${
      isScrolled
        ? "bg-background/60 border-border/50 backdrop-blur-md"
        : "bg-background border-border"
    }`}>
      <div className="max-w-7xl mx-auto px-4 transition-colors duration-300 text-foreground">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <div className="flex items-center gap-4 lg:gap-8 min-w-0">
            <a href="/" className="flex items-center gap-2 shrink-0" data-testid="link-nav-home">
              <OptimizedImage
                src="/troopn-logo.png"
                alt="Troopn"
                width={36}
                height={36}
                loading="eager"
                pictureClassName="shrink-0"
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full"
                data-testid="img-nav-logo"
              />
              <div className="flex flex-col leading-tight min-w-0">
                <BrandWordmark />
                <div className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-navy" />
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-gold" />
                  <span className="w-1.5 h-1.5 rounded-full shrink-0 bg-brand-coral" />
                  <span className="text-[10px] ml-0.5 whitespace-nowrap text-muted-foreground" data-testid="text-nav-tagline">{t('navbar.tagline')}</span>
                </div>
              </div>
            </a>

            <div className="hidden lg:flex items-center gap-1">
              <a href="#how-it-works" className="text-sm font-medium px-2.5 py-2 rounded-md whitespace-nowrap hover-elevate" data-testid="link-nav-how-it-works">
                {t('nav.howItWorks')}
              </a>
              <a href="#destinations" className="text-sm font-medium px-2.5 py-2 rounded-md whitespace-nowrap hover-elevate" data-testid="link-nav-destinations">
                {t('nav.destinations')}
              </a>
              <a href="#features" className="text-sm font-medium px-2.5 py-2 rounded-md whitespace-nowrap hover-elevate" data-testid="link-nav-features">
                {t('nav.features')}
              </a>
              <a href="#features" className="text-sm font-medium px-2.5 py-2 rounded-md whitespace-nowrap hover-elevate" data-testid="link-nav-pricing">
                {t('nav.pricing')}
              </a>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <div className="hidden lg:flex items-center gap-2">
              <SignInButton mode="redirect">
                <Button className="bg-brand-orange border-brand-orange text-white" data-testid="button-nav-login">
                  {t('nav.login')}
                </Button>
              </SignInButton>
              <SignUpButton mode="redirect">
                <Button data-testid="button-nav-signup">
                  {t('nav.signUp')}
                </Button>
              </SignUpButton>
            </div>
            <LanguageSwitcher />
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle menu"
              data-testid="button-mobile-menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 space-y-1">
            <a href="#how-it-works" className="block px-3 py-2 rounded-md hover-elevate text-sm font-medium" data-testid="link-nav-how-it-works-mobile">
              {t('nav.howItWorks')}
            </a>
            <a href="#destinations" className="block px-3 py-2 rounded-md hover-elevate text-sm font-medium" data-testid="link-nav-destinations-mobile">
              {t('nav.destinations')}
            </a>
            <a href="#features" className="block px-3 py-2 rounded-md hover-elevate text-sm font-medium" data-testid="link-nav-features-mobile">
              {t('nav.features')}
            </a>
            <a href="#features" className="block px-3 py-2 rounded-md hover-elevate text-sm font-medium" data-testid="link-nav-pricing-mobile">
              {t('nav.pricing')}
            </a>
            <div className="pt-2 flex items-center gap-2 px-3">
              <div className="flex-1">
                <SignUpButton mode="redirect">
                  <Button className="w-full" data-testid="button-nav-signup-mobile">
                    {t('nav.signUp')}
                  </Button>
                </SignUpButton>
              </div>
              <div className="flex-1">
                <SignInButton mode="redirect">
                  <Button className="w-full bg-brand-orange border-brand-orange text-white" data-testid="button-nav-login-mobile">
                    {t('nav.login')}
                  </Button>
                </SignInButton>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
